import React, {
	Suspense,
	useState,
	useEffect,
	useCallback,
	useRef,
	useMemo,
} from 'react';

import CssBaseline from '@material-ui/core/CssBaseline';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';

import {
	calculateApprovedGeofenceAcres,
	calculateCurrentFarmGeofencedArea,
	calculatePendingGeofenceAcres,
	calculateRejectedGeofenceAcres,
	detachDeletedPolygonIndexFromEnclosedPoints,
	insertNewEditablePolygon,
	resetPointMapTiles,
	resetPolygonMapTiles,
	setEditablePolygons,
	setIsDrawEnabled,
	setIsSelectedPolygonEditable,
	setIsSelectedPolygonSaved,
	setSelectedPoint,
	setSelectedPolygonIndex,
	updatePolygonDrawingStage,
} from 'reducers/awdView.reducer';
import {
	setIsAlertModalVisible,
	setSnackbarMessage,
	setSnackbarSeverity,
} from 'reducers/global.reducer';

import { useAppDispatch, useAppSelector } from 'store/hooks';

import { colorPrimaryBlue } from 'utils/constants';
import { lat2tile, lng2tile } from 'utils/helpers';

import { redirect } from 'actions/navigation';

import { EditablePolygon } from 'interfaces/maps.interface';
import { PointMetaData } from 'interfaces/awd.interface';
import {
	ReadOnlyURLSearchParams,
	useSearchParams,
} from 'customHooks/useSearchParams';
import useSeasonalConfigs from 'customHooks/useSeasonalConfigs';
import { AppDispatch } from 'store';

import Snackbar from 'widgets/Snackbar';
import Select from 'widgets/Select';

import Loader from 'sharedComponents/Loader';

import { smoothlyPanMapsTo } from 'utils/customPanTo';

import { mapOptions } from 'modules/CommonView/components/GoogleMapView';
import SugarcaneLegend from 'modules/CommonView/components/Legend';
import PolygonSelector from './components/PolygonSelector';

const GoogleMapView = React.lazy(() => import('./components/GoogleMapView'));
const ActionSidePanel = React.lazy(
	() => import('./components/ActionSidePanel'),
);

// const seasonDropdownOptions: { label: string; value: string }[] = [
// 	{ label: 'Sugarcane 2024', value: '323' },
// ];

export const updateSelectedPolygonIndex = ({
	dispatch,
	editablePolygons,
	isSelectedPolygonSaved,
	polygonIndex,
	pointsOfFarmId,
	searchParams,
	selectedPolygonIndex,
}: {
	dispatch: AppDispatch;
	editablePolygons: Record<string, EditablePolygon>;
	isSelectedPolygonSaved: boolean;
	polygonIndex: number;
	pointsOfFarmId: Record<string, PointMetaData>;
	searchParams: ReadOnlyURLSearchParams;
	selectedPolygonIndex: number | undefined;
}) => {
	if (
		!isSelectedPolygonSaved &&
		editablePolygons !== undefined &&
		Object.keys(editablePolygons).length > 0 &&
		polygonIndex !== selectedPolygonIndex
	) {
		dispatch(setIsAlertModalVisible(true));
		return;
	}
	dispatch(setSelectedPolygonIndex(polygonIndex));
	if (Array.isArray(editablePolygons[polygonIndex]?.parentPointSignatures)) {
		let firstParentPoint =
			editablePolygons[polygonIndex].parentPointSignatures![0];
		dispatch(setSelectedPoint(firstParentPoint));
		if (firstParentPoint !== undefined) {
			searchParams.set(
				'contentId',
				pointsOfFarmId[
					editablePolygons[polygonIndex].parentPointSignatures![0]
				].contentId,
			);
			const newPath = `programs?${searchParams.toString()}`;
			dispatch(redirect(newPath));
		}
	}
};

const CommonView = (): JSX.Element => {
	const type = useAppSelector((s) => s.router.location.query.type);
	const seasonDropdownOptions = useMemo(() => {
		switch (type) {
			case 'sugarcane':
				return [{ label: 'Sugarcane 2024', value: '323' }];
			case 'groundnut':
				return [{ label: 'Groundnut 2025', value: '324' }];
			case 'paddy':
				return [{ label: 'Paddy 2025', value: '327' }];
			case 'potato':
				return [{ label: 'Potato 2025', value: '332' }];
			default:
				return [];
		}
	}, [type]);
	const dispatch = useAppDispatch();
	const searchParams = useSearchParams();

	const clickTimerRef: any = useRef({});
	const googleRef: any = useRef(null);
	const mapsRef: any = useRef(null);
	const prevEditPathsRef: any = useRef(null);
	const polygonPlaceIdsOfFarmIdRef: any = useRef(new Set());
	const pointPlaceIdsOfFarmIdRef: any = useRef(new Set());
	const polygonMapTilesRef: any = useRef({});
	const pointMapTilesRef: any = useRef({});
	const suggestionGeometriesRef: any = useRef({});
	const tilesPlaceIdsRef: any = useRef({});

	const selectedPoint = useAppSelector((s) => s.awdView.selectedPoint);
	const pointsOfFarmId = useAppSelector((s) => s.awdView.pointsOfFarmId);
	const editablePolygons = useAppSelector((s) => s.awdView.editablePolygons);

	const polygonDrawingStage = useAppSelector(
		(s) => s.awdView.polygonDrawingStage,
	);
	const selectedPolygonIndex = useAppSelector(
		(s) => s.awdView.selectedPolygonIndex,
	);
	const isSelectedPolygonSaved = useAppSelector(
		(s) => s.awdView.isSelectedPolygonSaved,
	);
	const lat = useAppSelector((s) => s.router.location.query.lat);
	const lng = useAppSelector((s) => s.router.location.query.lng);
	const farmId = useAppSelector((s) => s.router.location.query.farmId);
	const eventId = useAppSelector((s) => s.router.location.query.eventId);
	const snackbarMessage = useAppSelector((s) => s.global.snackbarMessage);
	const snackbarSeverity = useAppSelector((s) => s.global.snackbarSeverity);
	const [, seasonId] = useSeasonalConfigs();

	const [isSnackbarVisible, setIsSnackbarVisible] = useState(false);
	const [isPolygonSelectorOpen, setIsPolygonSelectorOpen] = useState(false);
	const [smartPolygonSuggestionEnabled, setSmartPolygonSuggestionEnabled] =
		useState<Record<string, number>>({});
	// TODO: make zoom a URL param;
	const [zoom, setZoom] = useState(19);

	const editablePolygonsList = useMemo(
		() => Object.values(editablePolygons),
		[editablePolygons],
	);

	// TODO: make a new reducer for this
	const deleteSelectedPolygon = useCallback(() => {
		if (selectedPolygonIndex === undefined) return;
		const polygons = JSON.parse(JSON.stringify(editablePolygons));
		delete polygons[`${selectedPolygonIndex}`];
		dispatch(setEditablePolygons({ ...polygons }));
		dispatch(setIsSelectedPolygonEditable(false));
		dispatch(
			detachDeletedPolygonIndexFromEnclosedPoints(selectedPolygonIndex),
		);
		dispatch(setSelectedPolygonIndex(undefined));
	}, [dispatch, editablePolygons, selectedPolygonIndex]);

	const restoreSelectedPolygonToPrevEdit = useCallback(() => {
		const polygons = JSON.parse(JSON.stringify(editablePolygons));
		polygons[Number(selectedPolygonIndex)].geometry =
			prevEditPathsRef.current;
		dispatch(setEditablePolygons({ ...polygons }));
	}, [dispatch, editablePolygons, selectedPolygonIndex]);

	const storeSelectedPolygonRecentSavedPath = useCallback(() => {
		if (
			selectedPolygonIndex === undefined ||
			editablePolygonsList?.length === 0 ||
			(editablePolygonsList?.[selectedPolygonIndex] as any)?.geometry
				?.length === 0 ||
			!prevEditPathsRef
		) {
			prevEditPathsRef.current = null;
			return;
		}
		prevEditPathsRef.current = JSON.parse(
			JSON.stringify(editablePolygons?.[selectedPolygonIndex]?.geometry),
		);
	}, [editablePolygons, editablePolygonsList, selectedPolygonIndex]);

	const addToPolygonPlaceIdsOfFarmIdRef = useCallback(
		(polygonPlaceIds: number[]) => {
			polygonPlaceIdsOfFarmIdRef.current = new Set([
				...Array.from(polygonPlaceIdsOfFarmIdRef.current),
				...polygonPlaceIds,
			]);
		},
		[],
	);

	const addToPointPlaceIdsOfFarmIdRef = useCallback(
		(pointPlaceIds: number[]) => {
			pointPlaceIdsOfFarmIdRef.current = new Set([
				...Array.from(pointPlaceIdsOfFarmIdRef.current),
				...pointPlaceIds,
			]);
		},
		[],
	);

	const insertSuggestedPolygonToEditablePolygon = useCallback(
		({
			suggestedPolygon,
			parentPoint,
		}: {
			suggestedPolygon: google.maps.Polygon;
			parentPoint: string;
		}): void => {
			const newPolygonPath = suggestedPolygon
				.getPath()
				.getArray()
				.map((coord: any) => {
					return {
						lat: coord.lat(),
						lng: coord.lng(),
					};
				});
			const temp: EditablePolygon = {
				isPolygonLocked: 'UNBLOCKED',
				polygonApprovalStatus: 'GEOFENCING_PENDING',
				geometry: newPolygonPath,
				placeId: undefined,
				parentPointSignatures: [parentPoint],
			};

			dispatch(insertNewEditablePolygon(temp));
			// dispatch(
			// 	updatePointData({
			// 		pointIdentifier: selectedPoint,
			// 		payload: {
			// 			geofencedAcres:
			// 				google.maps.geometry.spherical.computeArea(
			// 					suggestedPolygon.getPath(),
			// 				) * AREA_CONVERSION_FACTOR,
			// 		},
			// 	}),
			// );
			dispatch(setIsSelectedPolygonSaved(false));
			dispatch(setIsSelectedPolygonEditable(true));
			dispatch(updatePolygonDrawingStage('IN_PROGRESS'));
		},
		[dispatch],
	);

	const getSmartSuggestionPolygon = useCallback(
		(latLng: google.maps.LatLng | null, zoom = 19): void => {
			if (!selectedPoint) {
				dispatch(setSnackbarMessage('No point selected!'));
				dispatch(setSnackbarSeverity('error'));
				return;
			}
			if (smartPolygonSuggestionEnabled[selectedPoint] === -1) {
				return;
			}
			if (smartPolygonSuggestionEnabled[selectedPoint] === 0) {
				dispatch(
					setSnackbarMessage(
						'All suggestions are exhausted! Switching to drawing mode!',
					),
				);
				dispatch(setSnackbarSeverity('info'));
				dispatch(setIsDrawEnabled(true));
				dispatch(updatePolygonDrawingStage('IN_PROGRESS'));
			} else {
				setSmartPolygonSuggestionEnabled((prev) => {
					if (prev[`${selectedPoint}`] > 0) {
						return {
							...prev,
							[`${selectedPoint}`]: prev[`${selectedPoint}`] - 1,
						};
					}
					return prev;
				});
				let suggestionFound = false;
				let xTile = lng2tile(latLng?.lng(), zoom);
				let yTile = lat2tile(latLng?.lat(), zoom);
				if (tilesPlaceIdsRef.current[`${xTile}-${yTile}`]) {
					const polygonsOnTile: string[] = Array.from(
						tilesPlaceIdsRef.current[`${xTile}-${yTile}`],
					);
					for (let i = 0; i < polygonsOnTile.length; i++) {
						let curPolygonData =
							suggestionGeometriesRef.current[polygonsOnTile[i]];
						if (curPolygonData !== undefined) {
							let suggestedPolygon = new google.maps.Polygon({
								paths: curPolygonData?.placeGeometryResult
									.geometry,
							});
							if (
								google.maps.geometry.poly.containsLocation(
									latLng,
									suggestedPolygon,
								)
							) {
								suggestionFound = true;
								insertSuggestedPolygonToEditablePolygon({
									suggestedPolygon,
									parentPoint: selectedPoint,
								});
								suggestedPolygon.setMap(null);
								break;
							}
						}
					}
				}
				if (!suggestionFound) {
					dispatch(
						setSnackbarMessage(
							'No polygon suggestions found! Switching to drawing mode!',
						),
					);
					dispatch(setSnackbarSeverity('info'));
					dispatch(setIsDrawEnabled(true));
					dispatch(updatePolygonDrawingStage('IN_PROGRESS'));
				}
			}
		},
		[
			dispatch,
			insertSuggestedPolygonToEditablePolygon,
			selectedPoint,
			smartPolygonSuggestionEnabled,
		],
	);

	const handleClickOnMap = useCallback(() => {
		clickTimerRef.current = setTimeout(() => {
			setSmartPolygonSuggestionEnabled((prev) => {
				if (prev[`${selectedPoint}`] > 0) {
					return {
						...prev,
						[`${selectedPoint}`]: prev[`${selectedPoint}`] - 1,
					};
				}
				return prev;
			});
			if (
				selectedPoint !== undefined &&
				polygonDrawingStage === 'SUGGESTION'
			) {
				dispatch(setIsDrawEnabled(true));
				dispatch(updatePolygonDrawingStage('IN_PROGRESS'));
			}
		}, 300);
	}, [dispatch, polygonDrawingStage, selectedPoint]);

	const handleDblClickOnMap = useCallback(
		(e: google.maps.MapMouseEvent): void => {
			if (
				selectedPoint !== undefined &&
				polygonDrawingStage === 'SUGGESTION'
			) {
				clearTimeout(clickTimerRef.current);
				dispatch(setIsDrawEnabled(false));
				getSmartSuggestionPolygon(e.latLng, zoom);
			}
		},
		[
			dispatch,
			getSmartSuggestionPolygon,
			polygonDrawingStage,
			selectedPoint,
			zoom,
		],
	);

	const handleZoomChange = useCallback(() => {
		if (mapsRef.current === null) return;
		const currentZoom = mapsRef.current?.getZoom();
		setZoom(currentZoom);
	}, [setZoom]);

	const updateSuggestionGeometriesRef = useCallback((newGeometries) => {
		suggestionGeometriesRef.current = {
			...suggestionGeometriesRef.current,
			...newGeometries,
		};
	}, []);

	const handleCloseSnackbar = (
		event?: React.SyntheticEvent | Event,
		reason?: string,
	) => {
		if (reason === 'clickaway') {
			return;
		}
		setIsSnackbarVisible(false);
		setSnackbarMessage('');
	};

	const updateSeasonParam = useCallback(
		(newSeasonValue: string) => {
			const newPath = `programs?lat=${lat}&lng=${lng}&farmId=${farmId}&eventId=${eventId}&&seasonId=${newSeasonValue}&type=groundnut`;
			dispatch(redirect(newPath));
		},
		[dispatch, eventId, farmId, lat, lng],
	);

	const updateSelectedPolygonIndexAndRedirect = ({
		index,
	}: {
		index: number;
	}) => {
		updateSelectedPolygonIndex({
			dispatch,
			editablePolygons,
			isSelectedPolygonSaved,
			polygonIndex: index,
			pointsOfFarmId,
			searchParams,
			selectedPolygonIndex,
		});
		smoothlyPanMapsTo({
			mapInstance: mapsRef.current,
			mapOptions,
			lat: editablePolygons[index].geometry[0].lat,
			lng: editablePolygons[index].geometry[0].lng,
			zoom,
		});
	};

	const updateGoogleRef = useCallback(
		(googleInstance: typeof google.maps) => {
			googleRef.current = googleInstance;
		},
		[],
	);

	const selectPolygonOfSelectedPoint = () => {
		// For selecting default 1st, 0th index geofence selected
		if (editablePolygons === undefined) return;
		let index: number | undefined;
		// eslint-disable-next-line no-restricted-syntax
		for (const [key] of Object.entries(editablePolygons)) {
			// finds out the polygon for selected point
			if (key) {
				index = Number(key);
				break;
			}
		}

		/**
		 For not setting default 0 index next time when polygon gets edited
		 with this condition selectedPolygonIndex will not change to 0 again
		*/
		if (selectedPolygonIndex === undefined) {
			dispatch(setSelectedPolygonIndex(index));
		}
	};

	const togglePolygonSelector = ({
		onClose,
		onOpen,
		toggleValue,
	}: {
		onClose?: (a?: any) => any;
		onOpen?: (a?: any) => any;
		toggleValue?: boolean;
	}) => {
		setIsPolygonSelectorOpen((prev: boolean) => {
			if (prev) {
				onClose && onClose();
			} else {
				onOpen && onOpen();
			}
			return toggleValue === undefined ? !prev : toggleValue;
		});
	};

	useEffect(() => {
		dispatch(resetPolygonMapTiles());
		dispatch(resetPointMapTiles());
		tilesPlaceIdsRef.current = {};
		suggestionGeometriesRef.current = {};
	}, [dispatch, tilesPlaceIdsRef, zoom]);

	useEffect(() => {
		selectPolygonOfSelectedPoint();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedPoint, editablePolygons]);

	useEffect(() => {
		if (googleRef.current !== null) {
			dispatch(calculateCurrentFarmGeofencedArea());
			dispatch(calculateApprovedGeofenceAcres());
			dispatch(calculatePendingGeofenceAcres());
			dispatch(calculateRejectedGeofenceAcres());
		}
	}, [dispatch, editablePolygons]);

	useEffect(() => {
		if (selectedPoint === undefined) return;
		dispatch(setIsSelectedPolygonEditable(false));
	}, [dispatch, selectedPoint]);

	useEffect(() => {
		return () => {
			dispatch(setSelectedPolygonIndex(undefined));
			prevEditPathsRef.current = null;
			polygonPlaceIdsOfFarmIdRef.current = new Set();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (snackbarMessage.length > 0) {
			setIsSnackbarVisible(true);
		}
	}, [snackbarMessage]);

	return (
		<div style={{ width: '100vw', height: '100vh' }}>
			<CssBaseline />
			<Suspense fallback={<Loader loading />}>
				<Box>
					<SugarcaneLegend />
				</Box>
				<Box>
					<Select
						containerStyles={{
							fontFamily: 'Poppins',
							position: 'absolute',
							left: '400px',
							zIndex: 1,
							boxShadow: '0px 8px 8px rgba(0, 0, 0, 0.25)',
							borderRadius: '4px',
							background: colorPrimaryBlue,
						}}
						width={180}
						labelStyles={{
							color: 'rgba(255, 255, 255, 0.9)',
							fontFamily: 'Poppins',
						}}
						options={seasonDropdownOptions}
						placeholder="Select a season"
						selectInputStyles={{
							color: 'rgba(255, 255, 255, 0.9)',
							fontFamily: 'Poppins',
						}}
						selectLabel="Year"
						setValue={updateSeasonParam}
						value={seasonId}
					/>
				</Box>
				<Grid container spacing={0} style={{ height: '100%' }}>
					<div style={{ width: '70%', position: 'relative' }}>
						<GoogleMapView
							addToPointPlaceIdsOfFarmIdRef={
								addToPointPlaceIdsOfFarmIdRef
							}
							addToPolygonPlaceIdsOfFarmIdRef={
								addToPolygonPlaceIdsOfFarmIdRef
							}
							handleClick={
								polygonDrawingStage === 'PRE_START'
									? undefined
									: handleClickOnMap
							}
							handleDblClick={
								polygonDrawingStage === 'PRE_START'
									? undefined
									: handleDblClickOnMap
							}
							mapsRef={mapsRef}
							pointMapTilesRef={pointMapTilesRef}
							pointPlaceIdsOfFarmIdRef={pointPlaceIdsOfFarmIdRef}
							polygonMapTilesRef={polygonMapTilesRef}
							polygonPlaceIdsOfFarmIdRef={
								polygonPlaceIdsOfFarmIdRef
							}
							setSmartPolygonSuggestionEnabled={
								setSmartPolygonSuggestionEnabled
							}
							handleZoomChange={handleZoomChange}
							tilesPlaceIdsRef={tilesPlaceIdsRef}
							updateGoogleRef={updateGoogleRef}
							updateSuggestionGeometriesRef={
								updateSuggestionGeometriesRef
							}
							zoom={zoom}
						/>
						{editablePolygonsList.length > 0 && (
							<div
								style={{
									position: 'absolute',
									right: '10px',
									bottom: '110px',
								}}
							>
								<PolygonSelector
									activeItemIndex={selectedPolygonIndex}
									isOpen={isPolygonSelectorOpen}
									listItems={editablePolygonsList}
									onItemClick={
										updateSelectedPolygonIndexAndRedirect
									}
									orientation="horizontal"
									toggleCollapse={togglePolygonSelector}
								/>
							</div>
						)}
					</div>
					<div style={{ width: '30%', overflowY: 'scroll' }}>
						<ActionSidePanel
							addToPolygonPlaceIdsOfFarmIdRef={
								addToPolygonPlaceIdsOfFarmIdRef
							}
							deleteSelectedPolygon={deleteSelectedPolygon}
							restoreSelectedPolygonToPrevEdit={
								restoreSelectedPolygonToPrevEdit
							}
							storeSelectedPolygonRecentSavedPath={
								storeSelectedPolygonRecentSavedPath
							}
						/>
					</div>
				</Grid>
				<Snackbar
					message={snackbarMessage}
					severity={snackbarSeverity}
					openSnackbar={isSnackbarVisible}
					handleClose={handleCloseSnackbar}
				/>
			</Suspense>
		</div>
	);
};

export default React.memo(CommonView);
